import { useGetScenarioQuery } from "api/TenantSlug/ConnectionCreator/hooks.ts";
import { formStructureApi } from "api/TenantSlug/FormStructure/api.ts";
import { DiagramWithStorage } from "components/CC/Diagrams/DiagramWithStorage/DiagramWithStorage.tsx";
import { CCEntityType } from "components/CC/types/entityConfig.ts";
import { QueryState } from "components/UI/QueryState/QueryState.tsx";
import { SectionContentWrapper } from "components/UI/SectionContentWrapper";
import { TenantPage } from "components/UI/TenantPage/TenantPage.tsx";
import { useFetchAllPaginatedData } from "hooks/useFetchAllPaginatedData";
import { ConnectionCreatorScenarioCategory, PermissionEnum } from "types/enums.ts";
import { createTenantFileRoute } from "utils/createTenantFileRoute.ts";
import { mergeScenarioFormFields } from "utils/scenarioFormStructure";
import { getToolValidationFieldHeaderItems } from "utils/tenantHeaderItems.ts";

const CATEGORY_TO_ENTITY_TYPE: Partial<Record<ConnectionCreatorScenarioCategory, CCEntityType>> = {
    [ConnectionCreatorScenarioCategory.StructureValidate]: CCEntityType.StructureValidate,
    [ConnectionCreatorScenarioCategory.StructureModify]: CCEntityType.StructureModify,
    [ConnectionCreatorScenarioCategory.StructureAutocomplete]: CCEntityType.StructureAutocomplete,
};

export const Route = createTenantFileRoute(
    "/_authenticated/$tenantSlug/_layout/tool/_layout/validation/_layout/$scenarioId/_layout/editor/"
)({
    component: ValidationEditorPage,
    staticData: {
        permissions: [PermissionEnum.ToolScenarioFieldEditor],
        showInSideMenu: false,
        label: "pages.tool.scenario.validation.editor.title",
    },
    loader: ({ context: { t } }) => {
        return {
            breadcrumb: t("pages.tool.scenario.validation.editor.title"),
        };
    },
});

function ValidationEditorPage() {
    const { scenarioId } = Route.useParams();
    const { data, isLoading, isError } = useGetScenarioQuery(scenarioId, {
        filter: { include: "formStructures" },
    });
    const scenario = data?.scenario;
    const formStructureIds = scenario?.form_structures?.map((formStructure) => formStructure.id).filter(Boolean) ?? [];
    const scenarioFormStructuresQuery = useFetchAllPaginatedData(
        (payload) => formStructureApi.index(payload),
        {
            filter: {
                filter: { id: { in: formStructureIds } },
                include: "formFields",
            },
        },
        "formStructures",
        { enabled: formStructureIds.length > 0 }
    );

    if (!scenario) {
        return <QueryState isLoading={isLoading} isError={isError || !isLoading} errorKey="errors.loading_scenario" />;
    }

    const entityType = CATEGORY_TO_ENTITY_TYPE[scenario.category as ConnectionCreatorScenarioCategory];
    const isLoadingFormStructures = formStructureIds.length > 0 && scenarioFormStructuresQuery.isLoading;

    if (!entityType) {
        return <QueryState isLoading={false} isError={true} errorKey="errors.loading_scenario" />;
    }

    if (isLoadingFormStructures) {
        return <QueryState isLoading={true} isError={false} errorKey="errors.loading_scenario" />;
    }

    if (scenarioFormStructuresQuery.error) {
        return <QueryState isLoading={false} isError={true} errorKey="errors.loading_scenario" />;
    }

    const mergedFormFields = mergeScenarioFormFields(scenarioFormStructuresQuery.data ?? []);

    return (
        <TenantPage headerNavigationItems={getToolValidationFieldHeaderItems} isEdit>
            <SectionContentWrapper withBackground>
                <DiagramWithStorage
                    key={scenarioId}
                    entityId={scenario.id}
                    entityType={entityType}
                    scenarioId={scenario.id}
                    formFields={mergedFormFields}
                />
            </SectionContentWrapper>
        </TenantPage>
    );
}
